import { createAction, ActionType } from 'typesafe-actions';
import {
    LooksWishlistResponse,
    FETCH_LOOKS_WISHLIST,
    FETCH_LOOKS_WISHLIST_SUCCESS,
    FETCH_LOOKS_WISHLIST_FAILED,
    ADD_TO_LOOKS_WISHLIST,
    ADD_TO_LOOKS_WISHLIST_SUCCESS,
    ADD_TO_LOOKS_WISHLIST_FAILED,
    REMOVE_FROM_LOOKS_WISHLIST,
    REMOVE_FROM_LOOKS_WISHLIST_FAILED,
    REMOVE_FROM_LOOKS_WISHLIST_SUCCESS,
    LOOKS_WISHLIST_QUEUE_UPDATED,
    ADD_QUEUED_LOOK_TO_WISHLIST,
    REMOVE_QUEUED_LOOK_FROM_WISHLIST,
} from './types';
import { DataFetchingThunkAction, BellaThunkDispatch, StateInterface } from 'Client/redux/types';
import { WishListNotificationMessages } from 'Client/components/ProductDetailPage/hooks/useProductsWishlist';
import { addNotification } from 'Client/redux/notifications/actions';
import { RetryError } from 'Common/errors';
import retry from 'Common/utils/retry';
import { getErrorCustomData } from 'Common/errors/CustomFetchApiError';

const isFetchingLooksWishlistAction = createAction(FETCH_LOOKS_WISHLIST);

const fetchLooksWishlistSuccessAction = createAction(
    FETCH_LOOKS_WISHLIST_SUCCESS,
    action => (response: LooksWishlistResponse) => action({ response })
);

const fetchLooksWishlistFailedAction = createAction(FETCH_LOOKS_WISHLIST_FAILED, action => (error: Error) =>
    action({ error })
);

export const fetchLooksWishlistAction = (
    controller?: AbortController,
    noRetry = false
): DataFetchingThunkAction => async (dispatch, _, { apiService, logger }) => {
    dispatch(isFetchingLooksWishlistAction());

    try {
        const repeatTimes = noRetry ? 0 : 2;
        const getLooksWishlist = retry(
            (ctrllr?: AbortController) => apiService.shopApi.getLooksWishlist(ctrllr),
            repeatTimes
        );
        const looksWishlistResponse = await getLooksWishlist(controller);
        dispatch(fetchLooksWishlistSuccessAction(looksWishlistResponse));
    } catch (err) {
        const errorInfo = RetryError.isRetryError(err) ? err.toJson() : getErrorCustomData(err);
        logger.error(err, { action: 'fetchLooksWishlistFailedAction', ...errorInfo });
        dispatch(fetchLooksWishlistFailedAction(err));
    }
};

const isAddingToLooksWishlistAction = createAction(ADD_TO_LOOKS_WISHLIST, action => (lookSlug: string) =>
    action({ lookSlug })
);

const addToLooksWishlistSuccessAction = createAction(ADD_TO_LOOKS_WISHLIST_SUCCESS);

const addToLooksWishlistFailedAction = createAction(
    ADD_TO_LOOKS_WISHLIST_FAILED,
    action => (error: Error, lookSlug: string) => action({ error, lookSlug })
);

const isRemovingFromLooksWishlistAction = createAction(REMOVE_FROM_LOOKS_WISHLIST, action => (lookSlug: string) =>
    action({ lookSlug })
);

const removeFromLooksWishlistSuccessAction = createAction(REMOVE_FROM_LOOKS_WISHLIST_SUCCESS);

const removeFromLooksWishlistFailedAction = createAction(
    REMOVE_FROM_LOOKS_WISHLIST_FAILED,
    action => (error: Error, lookSlug: string) =>
        action({
            error,
            lookSlug,
        })
);

export const updateLooksWishlistQueue = createAction(
    LOOKS_WISHLIST_QUEUE_UPDATED,
    action => (todo: string | null, lookSlug: string | null) =>
        action({
            todo,
            lookSlug,
        })
);

export const actions = {
    isFetchingLooksWishlistAction,
    fetchLooksWishlistSuccessAction,
    fetchLooksWishlistFailedAction,
    isAddingToLooksWishlistAction,
    addToLooksWishlistSuccessAction,
    addToLooksWishlistFailedAction,
    isRemovingFromLooksWishlistAction,
    removeFromLooksWishlistFailedAction,
    updateLooksWishlistQueue,
    removeFromLooksWishlistSuccessAction,
};

export const addToLooksWishlistAction = (
    lookSlug: string,
    notificationMessages?: WishListNotificationMessages
): DataFetchingThunkAction => async (dispatch, _, { apiService, logger }) => {
    dispatch(isAddingToLooksWishlistAction(lookSlug));
    try {
        await apiService.shopApi.addToLooksWishlist(lookSlug);
        dispatch(addToLooksWishlistSuccessAction());
        if (notificationMessages) {
            dispatch(addNotification({ message: notificationMessages.successMessage }));
        }
    } catch (err) {
        logger.error(err, { action: 'addToLooksWishlistFailedAction', ...getErrorCustomData(err) });
        dispatch(addToLooksWishlistFailedAction(err, lookSlug));
        if (notificationMessages) {
            dispatch(addNotification({ message: notificationMessages.errorMessage, displayType: 'error' }));
        }
    }
};

export const removeFromLooksWishlistAction = (lookSlug: string): DataFetchingThunkAction => async (
    dispatch,
    _,
    { apiService, logger }
) => {
    dispatch(isRemovingFromLooksWishlistAction(lookSlug));
    try {
        await apiService.shopApi.removeFromLooksWishlist(lookSlug);
        dispatch(removeFromLooksWishlistSuccessAction());
    } catch (err) {
        logger.error(err, { action: 'removeFromLooksWishlistFailedAction', ...getErrorCustomData(err) });
        dispatch(removeFromLooksWishlistFailedAction(err, lookSlug));
    }
};

export const fetchOrAddLooksWishlist = () => async (dispatch: BellaThunkDispatch, getState: () => StateInterface) => {
    await dispatch(fetchLooksWishlistAction());
    const { header, looksWishlistQueue } = getState();
    const { looksWishlist } = header;
    if (
        looksWishlistQueue.lookSlug &&
        !looksWishlist.looks.includes(looksWishlistQueue.lookSlug) &&
        looksWishlistQueue.todo === ADD_QUEUED_LOOK_TO_WISHLIST
    ) {
        await dispatch(addToLooksWishlistAction(looksWishlistQueue.lookSlug));
    }
    dispatch(updateLooksWishlistQueue(null, null));
};

// Removes looks from wishlist store only, no api called.
// E.g.: used via event system when the api call is already issued in a micro frontend.
export const removeFromWishlistStoreAction = (slug: string) => (
    dispatch: BellaThunkDispatch,
    getState: () => StateInterface
) => {
    const { appShellLogin } = getState();
    if (appShellLogin.isLoggedIn) {
        dispatch(isRemovingFromLooksWishlistAction(slug));
        dispatch(removeFromLooksWishlistSuccessAction());
    } else {
        dispatch(updateLooksWishlistQueue(REMOVE_QUEUED_LOOK_FROM_WISHLIST, slug));
    }
};

export type LooksWishlistActionType = ActionType<typeof actions>;
